import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';

const STORAGE_KEY = 'xvpst_framework_edit_mode';

interface EditModeContextValue {
  /** ID of the change set the user is currently editing, or null if in Live View */
  changeSetId: string | null;
  /** Convenience boolean — true when changeSetId is set */
  isEditMode: boolean;
  /** Enter edit mode for the given change set ID (call after POST /change-sets) */
  enterEditMode: (id: string) => void;
  /** Exit edit mode (does NOT delete the change set on the server) */
  exitEditMode: () => void;
}

const EditModeContext = createContext<EditModeContextValue | null>(null);

export function EditModeProvider({ children }: { children: ReactNode }) {
  const [changeSetId, setChangeSetId] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    return window.localStorage?.getItem(STORAGE_KEY) || null;
  });

  // Persist across reloads so an editor doesn't lose their session
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (changeSetId) {
      window.localStorage.setItem(STORAGE_KEY, changeSetId);
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  }, [changeSetId]);

  const enterEditMode = useCallback((id: string) => {
    setChangeSetId(id);
  }, []);

  const exitEditMode = useCallback(() => {
    setChangeSetId(null);
  }, []);

  return (
    <EditModeContext.Provider
      value={{
        changeSetId,
        isEditMode: changeSetId !== null,
        enterEditMode,
        exitEditMode,
      }}
    >
      {children}
    </EditModeContext.Provider>
  );
}

export function useEditMode(): EditModeContextValue {
  const ctx = useContext(EditModeContext);
  if (!ctx) {
    throw new Error('useEditMode must be used inside an <EditModeProvider>');
  }
  return ctx;
}
